"use client";
import React from "react";
import DegreeStatisticalInfoBox from "./variants/DegreeStatistical";
import StatisticalInfoBox from "./variants/Statistical";
import { InfoBoxProps } from "./types";
import InformationalInfoBox from "./variants/Informational";
import DirectionalInfoBox from "@/components/InfoBox/variants/Directional";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import NestedInfoBox from "./variants/Nested";
import LinkedInfoBox from "./variants/Linked";

export default function InfoBox({
  variant = "informational",
  orientation = "icon-first",
  children,
  ...props
}: InfoBoxProps) {
  switch (variant) {
    case "statistical":
      return <StatisticalInfoBox {...props}>{children}</StatisticalInfoBox>;
    case "degree-statistical":
      return (
        <DegreeStatisticalInfoBox {...props}>
          {children}
        </DegreeStatisticalInfoBox>
      );
    case "directional":
      return (
        <DirectionalInfoBox {...props} orientation={orientation}>
          {children}
        </DirectionalInfoBox>
      );
    case "nested":
      return <NestedInfoBox {...props}>{children}</NestedInfoBox>;
    case "linked":
      return <LinkedInfoBox {...props} />;
    case "learn-informational":
      return (
        <FeatureFlagComponentResolverClient
          flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
          variants={{
            legacy: null,
            test: (
              <MonarchResolver<LearnPageRedesignTestResult>
                experimentKey="useLearnPageRedesign"
                variants={{
                  control: <> </>,
                  test: (
                    <MonarchDataConsumer<LearnPageRedesignTestResult>
                      experimentKey="useLearnPageRedesign"
                      render={(testResult) => {
                        if (
                          !testResult?.data?.informationalBlock ||
                          !testResult.data.informationalBlock.enabled
                        ) {
                          return <> </>;
                        }
                        return (
                          <InformationalInfoBox {...props}>
                            {children}
                          </InformationalInfoBox>
                        );
                      }}
                    />
                  ),
                }}
                Loader={null}
                fallbackVariant="control"
              />
            ),
          }}
          fallback="legacy"
        />
      );
    case "learn-linked":
      return (
        <FeatureFlagComponentResolverClient
          flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
          variants={{
            legacy: null,
            test: (
              <MonarchResolver<LearnPageRedesignTestResult>
                experimentKey="useLearnPageRedesign"
                variants={{
                  control: <> </>,
                  test: (
                    <MonarchDataConsumer<LearnPageRedesignTestResult>
                      experimentKey="useLearnPageRedesign"
                      render={(testResult) => {
                        if (
                          !testResult?.data?.linkedBlock ||
                          !testResult.data.linkedBlock.enabled
                        ) {
                          return <> </>;
                        }
                        return <LinkedInfoBox {...props} />;
                      }}
                    />
                  ),
                }}
                Loader={null}
                fallbackVariant="control"
              />
            ),
          }}
          fallback="legacy"
        />
      );
    case "learn-nested":
      return (
        <FeatureFlagComponentResolverClient
          flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
          variants={{
            legacy: null,
            test: (
              <MonarchResolver<LearnPageRedesignTestResult>
                experimentKey="useLearnPageRedesign"
                variants={{
                  control: <> </>,
                  test: (
                    <MonarchDataConsumer<LearnPageRedesignTestResult>
                      experimentKey="useLearnPageRedesign"
                      render={(testResult) => {
                        if (
                          !testResult?.data?.nestedBlock ||
                          !testResult.data.nestedBlock.enabled
                        ) {
                          return <> </>;
                        }
                        return (
                          <NestedInfoBox {...props}>{children}</NestedInfoBox>
                        );
                      }}
                    />
                  ),
                }}
                Loader={null}
                fallbackVariant="control"
              />
            ),
          }}
          fallback="legacy"
        />
      );
    case "informational":
    default:
      return <InformationalInfoBox {...props}>{children}</InformationalInfoBox>;
  }
}
